import {
  Button,
  Chip,
  DropdownMenu,
  ImportIcon,
  LockIcon,
  Modal,
  Row,
  showToast,
  Table,
  TableColumns,
  Tooltip,
  useDefaultSelecting,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { useConfirm } from '../../../../hooks/useConfirm';
import { useFilterTags } from '../../../../hooks/useFilterTags';
import { useHiddenColumns } from '../../../../hooks/useHiddenColumns';
import { useUserConfigContext } from '../../../../hooks/useUserConfigContext';
import { LessonsWithoutCardsQuery } from '../../../../types/planung-graphql-client-defs';
import { getTimetableCardType } from '../../../../utils/getTimetableCardType';
import { showSuccessDuplicateToast } from '../../../../utils/toast';
import { useTimetableStore } from '../../../TimetableGrid/TimetableProvider';
import { TimetableCardsForm } from '../../../TimetableVersion/TimetableVersionLessonCards/TimetableCardsForm';
import { MergeSubjectContainerLessonsModal } from '../../modals/MergeSubjectContainerLessonsModal/MergeSubjectContainerLessonsModal';
import { ImportLessonsToTimetableVersionModal } from '../../Tables/LessonsImport/ImportLessonsToTimetableVersionModal';
import { TimetableVersionLessonForm } from '../Forms/TimetableVersionLessonForm';
import { TimetableVersionSubjectContainerLessonForm } from '../Forms/TimetableVersionSubjectContainerLessonForm';
import { useLessonStore } from '../TimetableVersionLessonProvider';
import { VersionLessonTableType } from '../TimetableVersionLessonStore';
import { TimetableVersionLessonsTableCardCell } from './TimetableVersionLessonsTableCardCell';
import { TimetableVersionLessonsTableLastCol } from './TimetableVersionLessonsTableLastCol';

export type CardsInfoLesson = {
  uuid: string;
  groups: { uuid: string; shortName: string }[];
  usedDivision?: {
    groupsConnection: {
      edges: Array<{ node: { uuid: string; name: string } }>;
    };
    uuid: string;
  } | null;
  classes?: Array<{ uuid: string; name: string; shortName: string }>;
  teachers?: Array<{ uuid: string; name: string; color: string }>;
  subject?: string;
};

export type CardsInfo = {
  uuid: string;
  type: 'FS' | 'Container' | 'Plan';
  typeLabel: string;
  typeLabelShort: string;
  cardLabel: string;
  cardLabelShort: string;
  colors: string[];
  counter: number;
  duration: number;
  lesson?: CardsInfoLesson | null;
};

export type ClassInterfaceType = {
  uuid?: string;
  grade?: number;
  gradeGroup?: { uuid?: string; name: string; shortName?: string } | null;
  name: string;
  shortName?: string;
};

export type TeachingLoadInfo = {
  deputatByTeacher: Map<string, number>;
  deputatByClass: Map<string, number>;
  deputat: number;
  fachstundenDeputat: number;
  epochenDeputat: number;
  studentHours: number;
  teacherHours: number;
  teachingBlockWeeks: number;
  teachingBlocks: number;
};

export type LessonTableType = {
  uuid: string;
  subject: string;
  subjectShortName: string;
  classSort: string;
  teacherSort?: string;
  subjectUuid: string;
  editStatus?: 'unused' | 'inVersion' | 'placedCards' | 'blocked' | 'used';
  cardsInfos?: {
    defaultCards: CardsInfo[][];
    subjectContainerCards: CardsInfo[][];
  };
  classes: Array<
    ClassInterfaceType & {
      groups?: Array<{ uuid: string; shortName?: string; name: string; classFraction?: number }>;
    }
  >;
  classesUuids: string[];
  teachers: {
    uuid: string;
    name?: string | null;
    color?: string | null;
    teachingLoadEnabled: boolean;
    presenceEnabled: boolean;
    teachingLoadHours?: number;
  }[];
  teacherUuids: string[];
  elective: boolean;
  teachingLoadEnabled: boolean;
  timetableEnabled: boolean;
  onlyInTimetableVersion: boolean;
  lessonUnits?: {
    uuid: string;
    duration: number;
    count: number;
    subjectContainer?: { uuid: string; name: string; shortName: string } | null;
  }[];
  teachingLoadInfo?: TeachingLoadInfo;
  groupClassNames?: string;
  teachingLoadFactors?: string;
  placedCardsCount?: number | null;
  isSubjectContainerLesson?: boolean;
  curriculumUuid?: string;
  schoolYearUuid?: string;
  rooms?: { uuid: string; name: string }[];
  roomsUuids?: string[];
  cardInfoLesson?: CardsInfoLesson;
};

type LessonTableProps = {
  versionUuid: string;
  subjectContainerLessons?: boolean;
};

export const TimetableVersionLessonsTable = observer(({ versionUuid, subjectContainerLessons }: LessonTableProps) => {
  // hooks
  const { t } = useTranslation();
  const schoolYear = useUserConfigContext().selectedSchoolYear;
  const store = useTimetableStore();
  const { confirm, ConfirmationDialog } = useConfirm();
  const lessonStore = useLessonStore();
  const { pimAuthClaims } = useAuthClaims();

  const { columnVisibility, saveColumnVisibility } = useHiddenColumns(
    `lessons-version-table-${subjectContainerLessons ? 'subjectContainerLessons' : ''}`,
    {
      elective: false,
      teachingLoadEnabled: false,
      timetableEnabled: false,
      rooms: false,
    },
  );
  const { rowSelection, onRowSelectionChange } = useDefaultSelecting();

  // state
  const [loading, setLoading] = useState<boolean>(false);
  const [lessonsToMerge, setLessonsToMerge] = useState<VersionLessonTableType[]>([]);
  const [mergeSubjectContainerLessonsModalOpen, setMergeSubjectContainerLessonsModalOpen] = useState<boolean>(false);
  const [currentLessonUuid, setCurrentLessonUuid] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [lessonCardsModalOpen, setLessonCardsModalOpen] = useState<boolean>(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const _lessonsData = lessonStore.lessons;
  const lessonsData: VersionLessonTableType[] = Array.from(_lessonsData.values());

  const { memoFilter: dataFilter, filterTags } = useFilterTags(versionUuid);

  // if teachingBlock is true filter and sort by subject name and then by classSort
  const data = lessonsData
    .filter(dataFilter)
    .filter((l) => {
      return subjectContainerLessons ? l.isSubjectContainerLesson : !l.isSubjectContainerLesson;
    })
    .sort((a, b) => {
      if (subjectContainerLessons) {
        return a.subject.localeCompare(b.subject) || a.classSort.localeCompare(b.classSort);
      }
      return a.classSort.localeCompare(b.classSort) || a.subject.localeCompare(b.subject);
    });

  const currentVersion = store.currentVersion;

  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const handleDelete = async (lessons: VersionLessonTableType[]) => {
    const placedCards = lessons.some((l) => l.placedCardsCount && l.placedCardsCount > 0);
    const text = placedCards
      ? t('lesson.confirm.delete.hasPlacedCards', { count: lessons.length })
      : t('lesson.confirm.delete.default', { count: lessons.length });

    const confirmed = await confirm({ message: text });

    if (confirmed) {
      setLoading(true);
      try {
        await Promise.all(
          lessons.map((l) => {
            if (l.onlyInTimetableVersion) {
              return lessonStore.deleteLesson(l.uuid, versionUuid);
            }
            return lessonStore.deactivateLesson(l.uuid, versionUuid);
          }),
        );
        setLoading(false);
        refetchCards()
          .then(() => {
            setLoading(false);
          })
          .catch(() => {
            showToast(t('lesson.message.delete.error'), { type: 'error' });
            setLoading(false);
          });
      } catch {
        showToast(t('lesson.message.delete.error'), { type: 'error' });
        setLoading(false);
      }
    }
  };

  const refetchCards = useCallback(async () => {
    if (currentVersion && schoolYear) {
      const cards = await getTimetableCardType(currentVersion.uuid, schoolYear.uuid);
      store.initCards(cards);
      return cards;
    }
    return null;
  }, [currentVersion, schoolYear, store]);

  const handleDuplicate = async (lessons: VersionLessonTableType[]) => {
    const versionLessons = lessons.filter((l) => l.onlyInTimetableVersion);
    const notVersionLessons = lessons.filter((l) => !l.onlyInTimetableVersion);
    const confirmed = await confirm({
      title: versionLessons ? t('timetableVersion.editInVersion') : t('common.duplicate'),
      message: versionLessons
        ? t('lesson.confirm.editInVersion.confirm', { count: lessons.length })
        : t('lesson.confirm.duplicate.confirm', { count: lessons.length }),
    });
    if (confirmed) {
      setLoading(true);
      if (notVersionLessons) {
        await Promise.all(
          notVersionLessons.map((l) => {
            return lessonStore.moveLessonToVersion(l.uuid, versionUuid, organizationUuid);
          }),
        );
      }
      if (versionLessons) {
        await Promise.all(
          versionLessons.map((l) => {
            return lessonStore.duplicateLesson(l.uuid, versionUuid, organizationUuid);
          }),
        );
      }
      refetchCards().then(() => {
        showSuccessDuplicateToast();
        setLoading(false);
      });
    }
  };

  const handleDeactivate = useCallback(
    async (lessons: VersionLessonTableType[]) => {
      const placedCards = lessons.some((l) => l.placedCardsCount && l.placedCardsCount > 0);

      const text = placedCards
        ? t('lesson.confirm.deactivate.hasPlacedCards', { count: lessons.length })
        : t('lesson.confirm.deactivate.default', { count: lessons.length });

      const confirmed = await confirm({ message: text });
      if (confirmed) {
        setLoading(true);
        await lessonStore.deactivateLesson(lessons[0].uuid, versionUuid);
        await refetchCards();
        setLoading(false);
      }
    },
    [t, confirm, lessonStore, versionUuid, refetchCards],
  );

  const handleDetach = async (uuids: string[]) => {
    setLoading(true);
    await lessonStore.detachLesson(uuids[0]);
    setLoading(false);
  };

  const handleAssign = async (item: VersionLessonTableType, teachers: { uuid: string }[]) => {
    const _teachers: {
      uuid: string;
      name: string | null;
      lastName: string;
      color?: string | null;
    }[] = [];
    setLoading(true);
    teachers.forEach((teacher) => {
      const t = store.teachers.get(teacher.uuid);
      if (t) {
        _teachers.push({
          uuid: t.uuid,
          name: t.displayNameShort ?? '',
          lastName: t.lastName,
          color: t.timetableConfig?.color,
        });
      }
    });
    await lessonStore.assignLessons([item.uuid], _teachers);
    setLoading(false);
  };

  const handleAdd = () => {
    setEditModalOpen(true);
  };

  const handleEdit = (row: Row<VersionLessonTableType>) => {
    setCurrentLessonUuid(row.original.uuid);
    setEditModalOpen(true);
  };

  const onClose = () => {
    setCurrentLessonUuid(null);
    setLessonCardsModalOpen(false);
    setEditModalOpen(false);
    setImportModalOpen(false);
  };

  const onSave = async () => {
    onClose();
    await refetchCards();
  };

  const onLessonEdited = async (data?: Pick<LessonsWithoutCardsQuery, 'lessons'>['lessons'][0]) => {
    setEditModalOpen(false);
    setLoading(true);
    if (data) {
      const lessons = lessonStore.convertLessonResponseToTableType([data]);
      lessonStore.addLesson(lessons[0]);
      await refetchCards();
    }
    setLoading(false);
  };

  const onLessonCreated = async (data?: Pick<LessonsWithoutCardsQuery, 'lessons'>['lessons'][0]) => {
    setEditModalOpen(false);
    setLoading(true);
    if (data) {
      const lessons = lessonStore.convertLessonResponseToTableType([data]);
      lessonStore.addLesson(lessons[0]);
      await refetchCards();
    }
    setLoading(false);
  };

  const handleImported = async ({ data, error }: { error?: unknown; data: string[] }) => {
    if (error) {
      showToast(t('lesson.message.import.error'), { type: 'error' });
      return;
    }
    setLoading(true);
    onClose();
    await lessonStore.addLessonsByUuids(data);
    await refetchCards().then(() => {
      showToast(t('lesson.message.import.success'), { type: 'success' });
      setLoading(false);
    });
  };

  const tableColumns = useMemo((): TableColumns<VersionLessonTableType>[] => {
    return [
      {
        header: t('lesson.table.subject'),
        id: 'subject',
        accessorKey: 'subject',
        meta: {
          filterName: t('lesson.table.subject'),
        },
        size: 250,
      },
      {
        header: t('common.hours'),
        id: 'cards',
        size: 150,
        canOverflow: true,
        enableSorting: false,
        cell: ({ row }) => <TimetableVersionLessonsTableCardCell uuid={row.original.uuid} key={row.original.uuid} />,
      },
      {
        header: t('lesson.table.classesGroups'),
        id: 'groupClassNames',
        size: 100,
        accessorKey: 'groupClassNames',
      },
      {
        header: t('lesson.table.teacher'),
        id: 'teachers',
        cell: ({ row }) => {
          const displayNamesShort = row.original.teachers.map((teacher) => teacher.name).join(', ');
          return (
            <Tooltip triggerStyle={{ display: 'inline' }} content={displayNamesShort}>
              {displayNamesShort}
            </Tooltip>
          );
        },
        accessorFn: (originalRow) => {
          const displayNamesShort = originalRow.teachers.map((teacher) => teacher.name);
          return displayNamesShort.join(', ');
        },
        size: 225,
      },
      {
        header: t('lesson.table.rooms'),
        id: 'rooms',
        cell: ({ row }) => {
          const displayNamesShort = (row.original.rooms?.map((room) => room.name) ?? []).join(', ');
          return (
            <Tooltip triggerStyle={{ display: 'inline' }} content={displayNamesShort}>
              {displayNamesShort}
            </Tooltip>
          );
        },
        accessorFn: (originalRow) => {
          const displayNamesShort = originalRow.rooms?.map((room) => room.name) ?? [];
          return displayNamesShort.join(', ');
        },
        size: 225,
      },
      {
        header: t('lesson.basics.isElective'),
        id: 'elective',
        accessorKey: 'elective',
        type: 'boolean',
        size: 80,
      },
      {
        header: t('lesson.basics.isTeachingLoadEnabled.short'),
        meta: {
          filterName: t('lesson.basics.isTeachingLoadEnabled.full'),
          tooltip: t('lesson.basics.isTeachingLoadEnabled.full'),
        },
        id: 'teachingLoadEnabled',
        accessorKey: 'teachingLoadEnabled',
        type: 'boolean',
        size: 80,
      },
      {
        header: t('deputate.title', { count: 1 }),
        id: 'editStatus',
        accessorKey: 'editStatus',
        meta: {
          filterName: t('common.editable.full'),
          tooltip: t('common.editable.full'),
        },
        size: 80,
        cell: ({ row }) => {
          return row.original.editStatus === 'blocked' ? (
            <Chip bgColor={'var(--chip-bg-color)'} value={<LockIcon className={'smallest'} />} />
          ) : (
            <></>
          );
        },
      },
    ];
  }, [t]);

  const bulkEdit = (rows: Row<VersionLessonTableType>[]) => {
    if (subjectContainerLessons) {
      return (
        <DropdownMenu
          data={[
            {
              label: t('lesson.actions.mergeSubjectContainerLessons'),
              onClick: () => {
                setLessonsToMerge(rows.map(({ original }) => original));
                setMergeSubjectContainerLessonsModalOpen(true);
              },
            },
            {
              label: t('timetableVersion.editInVersion'),
              disabled: rows.every((r) => r.original.onlyInTimetableVersion),
              onClick: async () => {
                await handleDuplicate(
                  rows.filter((r) => !r.original.onlyInTimetableVersion).map((row) => row.original),
                );
                onRowSelectionChange({});
              },
            },
            {
              label: t('common.duplicate'),
              disabled: rows.every((r) => !r.original.onlyInTimetableVersion),
              onClick: async () => {
                await handleDuplicate(rows.filter((r) => r.original.onlyInTimetableVersion).map((row) => row.original));
                onRowSelectionChange({});
              },
            },
            {
              type: 'ruler',
            },
            {
              label: t('common.delete'),
              color: 'error',
              onClick: async () => {
                const lessons = rows.map((row) => row.original);
                await handleDelete(lessons);
                onRowSelectionChange({});
              },
            },
          ]}
        />
      );
    }

    return (
      <DropdownMenu
        data={[
          {
            label: t('timetableVersion.editInVersion'),
            color: 'default',
            disabled: rows.every((r) => r.original.onlyInTimetableVersion),
            onClick: async () => {
              await handleDuplicate(rows.filter((r) => !r.original.onlyInTimetableVersion).map((row) => row.original));
              onRowSelectionChange({});
            },
          },
          {
            label: t('common.duplicate'),
            color: 'default',
            disabled: rows.every((r) => !r.original.onlyInTimetableVersion),
            onClick: async () => {
              await handleDuplicate(rows.filter((r) => r.original.onlyInTimetableVersion).map((row) => row.original));
              onRowSelectionChange({});
            },
          },
          {
            type: 'ruler',
          },
          {
            label: t('common.delete'),
            color: 'error',
            onClick: async () => {
              const lessons = rows.map((row) => row.original);
              await handleDelete(lessons);
              onRowSelectionChange({});
            },
          },
        ]}
      />
    );
  };

  const actionBarSettings = useMemo(() => {
    return {
      extendedActionsLeft: <>{filterTags.map((tag) => tag)}</>,
      extendedActionsRight: (
        <Button
          hierarchy={'tertiary'}
          disabled={store.readonly}
          onClick={() => setImportModalOpen(true)}
          icon={<ImportIcon />}
        >
          {t('lesson.import')}
        </Button>
      ),

      showExpertFilter: true,
      showAddButton: true,
      disableAddButton: store.readonly,
      showPrintButton: true,
      showBulkEdit: !store.readonly,
      marginBottom: 'var(--spacing-3)',
    };
  }, [filterTags, store.readonly, t]);

  return (
    <>
      <Table<VersionLessonTableType>
        showBorderRadius
        showShadow
        showPagination={true}
        pageSize={20}
        isDesktop={true}
        showSelect={true}
        showVisibility
        loading={loading}
        isOnWhite={false}
        onAddClick={handleAdd}
        onColumnVisibilityChange={saveColumnVisibility}
        columnVisibility={columnVisibility}
        printerSettings={{
          headline: pimAuthClaims.getProfile()?.organization.name,
          subline: `${t('lesson.title', { count: 2 })} - ${t('common.schoolYear')} ${schoolYear?.shortName}`,
          filename: `${t('lesson.title', { count: 2 })}_${schoolYear?.shortName}`,
        }}
        showActionBar
        actionBarSettings={actionBarSettings}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        bulkEditDropdownContent={bulkEdit}
        showSort={true}
        columns={tableColumns}
        data={data}
        lastColWidth='34px'
        lastCol={(row) => (
          <TimetableVersionLessonsTableLastCol
            key={row.original.uuid}
            row={row}
            handleAssign={handleAssign}
            handleDeactivate={handleDeactivate}
            handleDelete={handleDelete}
            handleDetach={handleDetach}
            handleDuplicate={handleDuplicate}
            handleEdit={handleEdit}
            setCurrentLessonUuid={setCurrentLessonUuid}
            setLessonCardsModalOpen={setLessonCardsModalOpen}
            updateCheckboxes={async ({ lessonUuid, type, value }) => {
              setLoading(true);
              await lessonStore.updateCheckboxes({ lessonUuid, type, value });
              setLoading(false);
            }}
          />
        )}
      />
      <Modal
        isOpen={editModalOpen}
        onRequestClose={() => onClose()}
        title={
          currentLessonUuid
            ? subjectContainerLessons
              ? t('epochs.editPlan')
              : t('lesson.edit')
            : subjectContainerLessons
              ? t('epochs.addPlan')
              : t('lesson.add')
        }
      >
        {subjectContainerLessons ? (
          <TimetableVersionSubjectContainerLessonForm
            lessonUuid={currentLessonUuid}
            onClose={onClose}
            versionUuid={versionUuid}
            onLessonEdited={async (response: {
              error?: unknown;
              data?: Pick<LessonsWithoutCardsQuery, 'lessons'>['lessons'][0];
            }) => {
              if (response.error) {
                showToast(t('lesson.message.edit.error'), { type: 'error' });
                return;
              }
              await onLessonEdited(response.data);
            }}
            onLessonCreated={async (response: {
              error?: unknown;
              data?: Pick<LessonsWithoutCardsQuery, 'lessons'>['lessons'][0];
            }) => {
              if (response.error) {
                showToast(t('lesson.message.edit.error'), { type: 'error' });
                return;
              }
              await onLessonCreated(response.data);
            }}
            defaultInitialValues={{
              timetableEnabled: true,
            }}
          />
        ) : (
          <TimetableVersionLessonForm
            closeForm={() => onClose()}
            onLessonEdited={async (response: {
              error?: unknown;
              data?: Pick<LessonsWithoutCardsQuery, 'lessons'>['lessons'][0];
            }) => {
              if (response.error) {
                showToast(t('lesson.message.edit.error'), { type: 'error' });
                return;
              }
              await onLessonEdited(response.data);
            }}
            onLessonCreated={async (response: {
              error?: unknown;
              data?: Pick<LessonsWithoutCardsQuery, 'lessons'>['lessons'][0];
            }) => {
              if (response.error) {
                showToast(t('lesson.message.edit.error'), { type: 'error' });
                return;
              }
              await onLessonCreated(response.data);
            }}
            lessonUuid={currentLessonUuid}
            defaultInitialValues={{ timetableEnabled: true }}
            versionUuid={versionUuid}
            lessonsData={lessonsData}
          />
        )}
      </Modal>
      <Modal
        isOpen={lessonCardsModalOpen && currentLessonUuid !== null}
        onRequestClose={() => onClose()}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        title={t('timetableVersion.editCards') as string}
      >
        {currentLessonUuid && (
          <TimetableCardsForm onClose={() => onClose()} lessonUuid={currentLessonUuid} onSave={() => onSave()} />
        )}
      </Modal>
      <ImportLessonsToTimetableVersionModal
        isOpen={importModalOpen}
        onClose={() => onClose()}
        onSave={(response) => handleImported(response)}
        versionUuid={versionUuid}
        subjectContainerLessons={subjectContainerLessons ?? false}
      />
      <MergeSubjectContainerLessonsModal
        open={mergeSubjectContainerLessonsModalOpen}
        onClose={() => {
          onRowSelectionChange({});
          setMergeSubjectContainerLessonsModalOpen(false);
        }}
        allHaveToBeInTimetableVersion={true}
        handleMerge={async ({
          newCards,
          lessonUuids,
          cardsFromLesson,
          removePlacedCards,
        }: {
          lessonUuids: string[];
          cardsFromLesson?: string | null;
          newCards?: { duration: number; count: number }[];
          removePlacedCards?: boolean;
        }) => {
          setLoading(true);

          await lessonStore.mergeLessons({
            newCards,
            lessonUuids,
            cardsFromLesson,
            removePlacedCards,
            versionUuid,
            organizationUuid,
          });

          onRowSelectionChange({});
          setMergeSubjectContainerLessonsModalOpen(false);
          await refetchCards();
          setLoading(false);
        }}
        lessons={lessonsToMerge.map((lesson) => {
          return {
            uuid: lesson.uuid,
            name: `${lesson.subject} - ${lesson.groupClassNames}`,
            subjectContainerUuid: lesson.subjectUuid,
            subjectName: lesson.subject,
            onlyInTimetableVersion: lesson.onlyInTimetableVersion,
            cards: <TimetableVersionLessonsTableCardCell key={lesson.uuid} uuid={lesson.uuid} />,
          };
        })}
      />
      <ConfirmationDialog />
    </>
  );
});
